import { findBestMatch, getRelatedQuestions, FAQEntry } from '../../utils/nlp';
import { faqData } from './faqData';
import { compareTwoStrings } from 'string-similarity';

class NLPService {
  private static instance: NLPService;
  private faqEntries: FAQEntry[];
  private readonly CONFIDENCE_THRESHOLD = 0.6;

  private constructor() {
    this.faqEntries = this.preprocessFAQData();
  }

  public static getInstance(): NLPService {
    if (!NLPService.instance) {
      NLPService.instance = new NLPService();
    }
    return NLPService.instance;
  }

  private preprocessFAQData(): FAQEntry[] {
    return faqData.map(faq => ({
      ...faq,
      keywords: this.extractKeywords(faq.question, faq.answer),
      variations: this.generateVariations(faq.question)
    }));
  }

  private extractKeywords(question: string, answer: string): string[] {
    const combined = `${question} ${answer}`;
    const words = combined.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/);
    
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 
      'of', 'with', 'by', 'about', 'like', 'through', 'over', 'before', 'after',
      'since', 'most', 'some', 'such', 'when', 'who', 'what', 'where', 'why',
      'how', 'all', 'any', 'both', 'each', 'few', 'more', 'much', 'many'
    ]);
    
    return [...new Set(words)]
      .filter(word => !stopWords.has(word) && word.length > 2);
  }

  private generateVariations(question: string): string[] {
    const variations: string[] = [];
    const normalizedQuestion = question.toLowerCase();

    // Question variations
    variations.push(
      `how do i ${normalizedQuestion}`,
      `how can i ${normalizedQuestion}`,
      `what is ${normalizedQuestion}`,
      `tell me about ${normalizedQuestion}`,
      `i need help with ${normalizedQuestion}`,
      `can you help me ${normalizedQuestion}`,
      `where can i ${normalizedQuestion}`,
      `when can i ${normalizedQuestion}`
    );

    // Intent variations
    if (normalizedQuestion.includes('find') || normalizedQuestion.includes('search')) {
      variations.push(
        `looking for ${normalizedQuestion.replace(/find|search/g, '')}`,
        `need to locate ${normalizedQuestion.replace(/find|search/g, '')}`,
        `where is ${normalizedQuestion.replace(/find|search/g, '')}`
      );
    }

    if (normalizedQuestion.includes('book') || normalizedQuestion.includes('schedule')) {
      variations.push(
        `make an appointment`,
        `set up a visit`,
        `arrange a meeting`,
        `plan a consultation`
      );
    }

    return variations;
  }

  private categorizeIntent(query: string): string[] {
    const intents: string[] = [];
    const normalizedQuery = query.toLowerCase();

    // Emergency intent
    if (normalizedQuery.includes('emergency') || 
        normalizedQuery.includes('urgent') || 
        normalizedQuery.includes('immediate')) {
      intents.push('emergency');
    }

    // Appointment intent
    if (normalizedQuery.includes('book') || 
        normalizedQuery.includes('schedule') || 
        normalizedQuery.includes('appointment')) {
      intents.push('appointment');
    }

    // Information intent
    if (normalizedQuery.includes('what') || 
        normalizedQuery.includes('how') || 
        normalizedQuery.includes('tell me')) {
      intents.push('information');
    }

    // Location intent
    if (normalizedQuery.includes('where') || 
        normalizedQuery.includes('location') || 
        normalizedQuery.includes('near')) {
      intents.push('location');
    }

    return intents;
  }

  public findAnswer(query: string): { 
    answer: FAQEntry | null;
    confidence: number;
    related: FAQEntry[];
    intents?: string[];
  } {
    // Categorize intent
    const intents = this.categorizeIntent(query);

    // Find best match
    const match = findBestMatch(query, this.faqEntries);
    
    if (!match || match.score < this.CONFIDENCE_THRESHOLD) {
      // If no good match, try to find partial matches based on keywords
      const queryKeywords = this.extractKeywords(query, '');
      const partialMatches = this.faqEntries.map(entry => {
        const keywordMatches = entry.keywords?.filter(keyword => 
          queryKeywords.some(qk => compareTwoStrings(qk, keyword) > 0.8)
        ).length || 0;
        return {
          entry,
          score: keywordMatches / (entry.keywords?.length || 1)
        };
      }).filter(m => m.score > 0.3);

      if (partialMatches.length > 0) {
        const bestPartialMatch = partialMatches.sort((a, b) => b.score - a.score)[0];
        return {
          answer: bestPartialMatch.entry,
          confidence: bestPartialMatch.score,
          related: this.getRelatedByCategory(bestPartialMatch.entry.category),
          intents
        };
      }

      return {
        answer: null,
        confidence: 0,
        related: this.getPopularInCategory(intents[0] || 'general'),
        intents
      };
    }

    return {
      answer: match.entry,
      confidence: match.score,
      related: this.getRelatedQuestions(match.entry),
      intents
    };
  }

  private getRelatedByCategory(category: string): FAQEntry[] {
    return this.faqEntries
      .filter(entry => entry.category === category)
      .slice(0, 3);
  }

  private getPopularInCategory(category: string): FAQEntry[] {
    return this.faqEntries
      .filter(entry => entry.category.toLowerCase().includes(category.toLowerCase()))
      .slice(0, 3);
  }

  public getRelatedQuestions(currentQuestion: FAQEntry): FAQEntry[] {
    return getRelatedQuestions(currentQuestion, this.faqEntries);
  }
}

export const nlpService = NLPService.getInstance();
